import { Loader2, RefreshCw, Sparkles } from "lucide-react";

import { usePrivacyMasking } from "@/components/common/Sensitive";
import { KaiMarkdown } from "@/features/kai/kaiMarkdown";
import { RailFadeScroller } from "@/features/kai/KaiSidePanel";
import { useKaiMarkdownActions } from "@/features/kai/useKaiMarkdownActions";
import { useKaiEntitySummary } from "@/hooks/useKaiEntitySummary";
import { useMainScrollEdge } from "@/hooks/useMainScrollEdge";
import { useRightRail } from "@/context/RightRailContext";
import { cn } from "@/lib/utils";
import { CollapsibleRailSection } from "@/shell/CollapsibleRailSection";
import {
  railSectionStackClassName,
  useRailStackPanel,
} from "@/shell/RailStackContext";

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface BookingKaiInsightCardProps {
  sourceId: string;
  /** Desktop stack card — inner scroller with edge fade (like Kai chat). */
  scrollInRail?: boolean;
}

/**
 * Right-rail card showing Kai's insight for a booking. Loads a cached summary
 * when fresh, otherwise streams a new one. The summary folds in the booking's
 * latest linked email thread server-side (see the Kai entity-summary prefetch).
 */
export function BookingKaiInsightCard({
  sourceId,
  scrollInRail = false,
}: BookingKaiInsightCardProps) {
  const { text, cachedAt, loading, streaming, error, status, regenerate } =
    useKaiEntitySummary("booking", sourceId, true);
  const mask = usePrivacyMasking();
  const actions = useKaiMarkdownActions();
  const scrollRef = useMainScrollEdge<HTMLDivElement>(
    scrollInRail ? `${sourceId}:${text.length}:${streaming}` : "static",
  );

  const isLoading = loading && !streaming && !text;
  const showContent = text.length > 0;

  const metaRow = (
    <div className="flex shrink-0 items-center justify-between gap-2 pb-2">
      <span className="text-[11px] text-muted-foreground">
        {streaming
          ? (status ?? "Writing insight…")
          : cachedAt
            ? `Updated ${timeAgo(cachedAt)}`
            : "Booking insight"}
      </span>
      <button
        type="button"
        onClick={regenerate}
        disabled={streaming || loading}
        aria-label="Regenerate insight"
        className="flex size-6 items-center justify-center rounded text-muted-foreground/80 hover:bg-accent/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <RefreshCw
          className={`size-3.5 ${streaming || loading ? "animate-spin" : ""}`}
        />
      </button>
    </div>
  );

  const body = (() => {
    if (error && !showContent) {
      return (
        <div className="flex flex-col gap-2 py-1">
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={regenerate}
            className="w-fit text-xs font-medium text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-1">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {status ?? "Gathering booking context…"}
          </span>
        </div>
      );
    }

    if (showContent) {
      return (
        <div className="pb-[var(--scroll-edge-bottom-fade-height)] pr-0.5">
          <KaiMarkdown
            text={text}
            ctx={{
              isStreaming: streaming,
              maskSensitive: mask,
              onSendFollowUp: actions.onSendFollowUp,
              beforeNavigate: actions.beforeNavigate,
            }}
          />
          {error && (
            <p className="mt-2 text-xs text-destructive">{error}</p>
          )}
        </div>
      );
    }

    return (
      <p className="text-sm text-muted-foreground">
        {status ?? "No insight yet."}
      </p>
    );
  })();

  if (!scrollInRail) {
    return (
      <div className="flex flex-col">
        {metaRow}
        {body}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {metaRow}
      <RailFadeScroller
        scrollRef={scrollRef}
        underRailHeader={false}
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
      >
        {body}
      </RailFadeScroller>
    </div>
  );
}

/** Right-rail collapsible card for Kai booking insight (booking detail). */
export function BookingKaiInsightRailSection({
  sourceId,
  stack = false,
  stackScroll = false,
  stackGrow = false,
}: {
  sourceId: string;
  stack?: boolean;
  /** Desktop stack layout (fixed rail height). */
  stackScroll?: boolean;
  /** Shares vertical space with other expanded stack cards. */
  stackGrow?: boolean;
}) {
  const { toggleBookingInsightSection } = useRightRail();
  const view = useRailStackPanel("insight");

  const scrollInRail =
    view.fill && ((!stack && !stackScroll) || (stackScroll && stackGrow));

  return (
    <CollapsibleRailSection
      title="Kai insight"
      icon={Sparkles}
      expanded={view.expanded}
      accordion={view.accordion}
      fill={view.fill}
      flow={view.flow}
      scroll={false}
      onToggle={toggleBookingInsightSection}
      className={cn(
        railSectionStackClassName(view, {
          mobileStack: stack,
          stackAnimate: stackScroll,
          stackGrow,
        }),
        view.fill && stackGrow && "flex min-h-0 flex-1 flex-col",
      )}
      contentClassName={stack ? undefined : "pr-0.5"}
    >
      <BookingKaiInsightCard sourceId={sourceId} scrollInRail={scrollInRail} />
    </CollapsibleRailSection>
  );
}
